import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { setLocale } from 'yup'

import {
  Typography,
  withStyles,
  Grid,
  Paper,
  MenuItem
} from '@material-ui/core'
import axios from 'axios'
import { format } from 'date-fns'
import {
  PhoneNumberUtil,
  PhoneNumberFormat as PNF
} from 'google-libphonenumber'

import config from '../../config'
import { Content } from '../atoms/Content'
import { BottomBar } from './BottomBar'
import {
  shouldShowDimensioniPneumatico,
  shouldShowTarga,
  getBookingState
} from '../../store/booking/selectors'
import { bookingSetUserData, bookingError } from '../../store/booking/actions'
import { TextField } from '../atoms/TextField'
import { LinkedBackButton, computeUrl } from '../atoms/BackButton'
import { WORKFLOW_STEP_1 } from '../../store/workflow/constant'
import { navigate } from '@reach/router'
import { LinkedBookingPreview } from './BookingPreview'
import { SubmitButton } from '../atoms/SubmitButton'
import {
  getAppPDV,
  getAppDomain,
  hasAlreadyBookedError
} from '../../store/app/selectors'
import { getGarageMandatoryFields } from '../../store/garage/selectors'

const styles = theme => ({
  root: {
    padding: theme.spacing.unit * 2
  },
  container: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit * 2
  },
  error: {
    color: 'red',
    fontWeight: 'bold',
    marginTop: theme.spacing.unit * 3,
    marginBottom: theme.spacing.unit
  },
  libretto: {
    width: '100%'
  },
  numberField: {
    '& input[type=number]': {
      '-moz-appearance': 'textfield'
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0
    }
  }
})

const CarDataItem = ({ showTarga, children }) =>
  showTarga ? (
    <Grid item xs={12} sm={4}>
      {children}
    </Grid>
  ) : (
    <Grid item xs={12} sm={6} md={3}>
      {children}
    </Grid>
  )

function validateTelefono(value) {
  let isValid = false
  try {
    const PhoneUtil = PhoneNumberUtil.getInstance()
    const parsedNum = PhoneUtil.parseAndKeepRawInput(value, 'IT')
    isValid = PhoneUtil.isValidNumber(parsedNum)
  } catch (e) {
    return false
  }
  return isValid
}

function hasMax1DecimalPlace(value) {
  if (value === undefined) return true
  var countDecimals = function(value) {
    if (Math.floor(value) === value) return 0
    return value.toString().split('.')[1].length || 0
  }

  return countDecimals(value) <= 1
}

export class UserFormBase extends Component {
  componentDidMount() {
    window.scrollTo(0, 0)
  }

  formatPhoneNumber = number => {
    const PhoneUtil = PhoneNumberUtil.getInstance()

    const parsedNum = PhoneUtil.parseAndKeepRawInput(number, 'IT')

    if (PhoneUtil.isValidNumber(parsedNum))
      return PhoneUtil.format(parsedNum, PNF.INTERNATIONAL)
    return `+39 ${number}`
  }

  formatDataToSend = values => {
    const { pdvID } = this.props

    let objectToSend = {
      ...values.userData,
      articoliList: values.services,
      data: format(values.selectedDay, 'YYYY-MM-DD'),
      orarioId: values.selectedHour.id
    }

    if (pdvID !== null) {
      return { ...objectToSend, pdv: pdvID }
    }
    return objectToSend
  }

  isAlreadyBooked = error =>
    error.request &&
    error.response &&
    error.response.status === 500 &&
    error.response.data.exceptionCode === 1056

  handleSubmit = async (values, actions) => {
    const { setUserData, domain, pdvID, handleError } = this.props

    try {
      values.userData.telefono = this.formatPhoneNumber(
        values.userData.telefono
      )

      const sentData = this.formatDataToSend(values)

      await axios.post(
        `${config.apiUrl}appuntamenti-web?domain=${domain}`,
        sentData
      )
      setUserData(values.userData)
      navigate(computeUrl('/success', pdvID))
    } catch (e) {
      handleError(e)
      actions.setSubmitting(false)
    }
  }

  addMandatoryRules = schema => {
    const { mandatoryFields } = this.props

    for (let field in schema) {
      if (mandatoryFields.find(f => f === field)) {
        schema[field] = schema[field].required('Campo obbligatorio')
      }
    }

    return schema
  }

  createValidationSchema = () => {
    setLocale({
      mixed: {
        default: 'Non valido'
      },
      string: {
        // eslint-disable-next-line
        max: 'Massimo ${max} caratteri'
      },
      number: {
        integer: 'Deve essere un numero intero',
        positive: 'Deve essere un valore positivo'
      }
    })

    const userDataschema = {
      nome: Yup.string().max(20),
      cognome: Yup.string().max(20),
      email: Yup.string()
        .email('Email non valida')
        .max(50),
      telefono: Yup.string().test({
        name: 'isValidPhone',
        excludeEmptyString: true,
        message: 'Numero non valido',
        test: validateTelefono
      }),
      marcaVeicolo: Yup.string().max(25),
      modelloVeicolo: Yup.string().max(25)
    }

    if (this.props.shouldShowDimensioniPneumatico) {
      userDataschema['larghezzaPneumatico'] = Yup.number()
        .transform((cv, ov) => {
          return ov === '' ? undefined : cv
        })
        .integer()
        .positive()
        .lessThan(1000, 'Massimo 3 cifre')
      userDataschema['altezzaPneumatico'] = Yup.number()
        .transform((cv, ov) => {
          return ov === '' ? undefined : cv
        })
        .integer()
        .positive()
        .lessThan(1000, 'Massimo 3 cifre')
      userDataschema['diametroPneumatico'] = Yup.number()
        .transform((cv, ov) => {
          return ov === '' ? undefined : cv
        })
        .positive()
        .lessThan(1000, 'Massimo 3 cifre intere')
        .test({
          name: 'hasMax1DecimalPlace',
          message: 'Massimo 1 cifra decimale',
          test: hasMax1DecimalPlace
        })
      userDataschema['indiceVelocitaPneumatico'] = Yup.string().max(2)
    }
    if (this.props.shouldShowTarga) {
      userDataschema['targaVeicolo'] = Yup.string().max(10)
    }

    this.addMandatoryRules(userDataschema)

    return Yup.object().shape({
      userData: Yup.object().shape(userDataschema)
    })
  }

  render() {
    const {
      classes,
      bookingData,
      shouldShowDimensioniPneumatico,
      shouldShowTarga,
      hasAlreadyBookedError
    } = this.props

    const initialValues = {
      ...bookingData,
      userData: {
        nome: '',
        cognome: '',
        email: '',
        telefono: '',
        marcaVeicolo: '',
        modelloVeicolo: '',
        larghezzaPneumatico: '',
        altezzaPneumatico: '',
        diametroPneumatico: '',
        indiceVelocitaPneumatico: '',
        stagionePneumatico: '',
        tipoPneumatico: '',
        targaVeicolo: ''
      }
    }

    const validationSchema = this.createValidationSchema()

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={this.handleSubmit}
        render={({ isSubmitting, submitForm, handleChange }) => (
          <div>
            <Content>
              <LinkedBookingPreview showDate />
              <Paper className={classes.root}>
                <Form>
                  <div className={classes.container}>
                    <Typography variant="h5" gutterBottom>
                      Inserisci i tuoi dati
                    </Typography>
                    <Grid container spacing={16}>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField
                          autoFocus
                          name="userData.nome"
                          label="Nome"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField name="userData.cognome" label="Cognome" />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField name="userData.email" label="Email" />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextField name="userData.telefono" label="Telefono" />
                      </Grid>
                    </Grid>
                  </div>

                  <div className={classes.container}>
                    <Typography variant="h5" gutterBottom>
                      I dati del tuo veicolo
                    </Typography>
                    <Grid container spacing={16}>
                      <CarDataItem showTarga={shouldShowTarga}>
                        <TextField name="userData.marcaVeicolo" label="Marca" />
                      </CarDataItem>
                      <CarDataItem showTarga={shouldShowTarga}>
                        <TextField
                          name="userData.modelloVeicolo"
                          label="Modello"
                        />
                      </CarDataItem>
                      {shouldShowTarga && (
                        <CarDataItem showTarga={shouldShowTarga}>
                          <TextField
                            name="userData.targaVeicolo"
                            label="Targa"
                          />
                        </CarDataItem>
                      )}
                    </Grid>
                  </div>

                  {shouldShowDimensioniPneumatico && (
                    <div className={classes.container}>
                      <Typography variant="h5" gutterBottom>
                        Dimensioni pneumatici
                      </Typography>
                      <Grid container spacing={16} justify="center">
                        <Grid item xs={12} sm={3}>
                          <TextField
                            name="userData.larghezzaPneumatico"
                            label="Larghezza"
                            type="number"
                            className={classes.numberField}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            name="userData.altezzaPneumatico"
                            label="Altezza"
                            type="number"
                            className={classes.numberField}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            name="userData.diametroPneumatico"
                            label="Diametro"
                            type="number"
                            className={classes.numberField}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            name="userData.indiceVelocitaPneumatico"
                            label="Indice velocità"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="userData.stagionePneumatico"
                            label="Stagione"
                            select
                          >
                            <MenuItem value="" />
                            <MenuItem value="estivi">Estivi</MenuItem>
                            <MenuItem value="invernali">Invernali</MenuItem>
                            <MenuItem value="4stagioni">4 stagioni</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="userData.tipoPneumatico"
                            label="Tipo pneumatico"
                            select
                          >
                            <MenuItem value="" />
                            <MenuItem value="auto">Auto</MenuItem>
                            <MenuItem value="4x4">4x4</MenuItem>
                            <MenuItem value="trasporto_leggero">
                              Trasporto leggero
                            </MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <img
                            src="libretto.png"
                            alt="libretto"
                            className={classes.libretto}
                          />
                          <Typography>
                            Dove trovare le dimesioni dei pneumatici sul tuo
                            libretto di circolazione
                          </Typography>
                        </Grid>
                      </Grid>
                    </div>
                  )}
                </Form>
                {hasAlreadyBookedError && (
                  <div className={classes.error}>
                    L'orario da te selezionato non è più disponibile. Seleziona
                    un diverso giorno/orario
                  </div>
                )}
              </Paper>
            </Content>
            <BottomBar>
              <SubmitButton onClick={submitForm} isSubmitting={isSubmitting} />
              <LinkedBackButton targetStep={WORKFLOW_STEP_1}>
                Modifica giorno
              </LinkedBackButton>
            </BottomBar>
          </div>
        )}
      />
    )
  }
}

export const UserForm = withStyles(styles)(UserFormBase)

const mapStateToProps = state => ({
  bookingData: getBookingState(state),
  shouldShowDimensioniPneumatico: shouldShowDimensioniPneumatico(state),
  shouldShowTarga: shouldShowTarga(state),
  domain: getAppDomain(state),
  pdvID: getAppPDV(state),
  hasAlreadyBookedError: hasAlreadyBookedError(state),
  mandatoryFields: getGarageMandatoryFields(state)
})

const mapDispatchToProps = dispatch => ({
  setUserData: userData => dispatch(bookingSetUserData(userData)),
  handleError: error => dispatch(bookingError(error))
})

export const LinkedUserForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(UserForm)
